//* 组件属性
import type { ComparisonOption } from "./index";

export const useOptions = (mode: string): ComparisonOption => {
    const i18n = useI18n();
    switch (mode) {
        case "model":
            return {
                name: i18n.t("ModelComparison"),
                route: "Model",
                error: i18n.t("AddedSameModel"),
                placeholder: i18n.t("AddNewModel"),
                option: [
                    {
                        visible: true,
                        key: "info",
                        name: i18n.t("Genetic"),
                        option: [
                            { key: "allele_type", name: i18n.t("AlleleType"), same: false },
                            { key: "strain", name: i18n.t("StrainOrigin"), same: false },
                            { key: "related_genes", name: i18n.t("RelatedGene"), same: false, route: "Gene" },
                            { key: "mutation", name: i18n.t("Mutation"), same: false },
                            { key: "methods", name: i18n.t("Inheritance"), same: false },
                        ],
                    },
                    {
                        visible: true,
                        key: "model_characterization",
                        name: i18n.t("Phenotypes", 2),
                        option: [
                            { key: "homozygous_characterization", name: i18n.t("Homozygote"), same: false, query: { module: "module2" } },
                            { key: "heterozygous_characterization", name: i18n.t("Heterozygote"), same: false, query: { module: "module2" } },
                        ],
                    },
                    {
                        visible: true,
                        key: "related_diseases",
                        name: i18n.t("RelatedDiseases"),
                        option: [{ key: "related_diseases", name: i18n.t("RelatedDiseases"), same: false, route: "Disease" }],
                    },
                    {
                        visible: true,
                        key: "references",
                        name: i18n.t("RelatedReferences"),
                        option: [{ key: "references", name: i18n.t("Quantity"), same: false }],
                    },
                ],
            };

        case "disease":
            return {
                name: i18n.t("DiseasesComparison"),
                route: "Disease",
                error: i18n.t("AddedSameDisease"),
                placeholder: i18n.t("AddNewDisease"),
                option: [
                    {
                        visible: true,
                        key: "info",
                        name: i18n.t("Genetic"),
                        option: [
                            { key: "methods", name: i18n.t("Inheritance"), same: false },
                            { key: "onset", name: i18n.t("AgeOnset"), same: false },
                            { key: "prevalence_incidence", name: i18n.t("Prevalence"), same: false },
                            { key: "anatomical_category", name: i18n.t("AnatomicalCategory"), same: false },
                            { key: "disease_category", name: i18n.t("GlobalCategory"), same: false },
                        ],
                    },
                    {
                        visible: true,
                        key: "disease_characterization",
                        name: i18n.t("DiseaseCharacterization"),
                        option: [
                            { key: "disease_characterization", name: i18n.t("DiseaseCharacterization"), same: false, query: { module: "module2" } },
                        ],
                    },
                    {
                        visible: true,
                        key: "related_genes",
                        name: i18n.t("RelatedGene"),
                        option: [{ key: "related_genes", name: i18n.t("RelatedGene"), same: false, route: "Gene", query: { module: "module3" } }],
                    },
                    {
                        visible: true,
                        key: "mutations",
                        name: i18n.t("Mutations"),
                        option: [
                            { key: "total", name: i18n.t("Statistics"), same: false },
                            { key: "pathogenic", name: i18n.t("Pathogenic"), same: false },
                            { key: "likely_pathogenic", name: i18n.t("LikelyPathogenic"), same: false },
                            { key: "likely_benign", name: i18n.t("LikelyBenign"), same: false },
                            { key: "benign", name: i18n.t("Benign"), same: false },
                            { key: "uncertain", name: i18n.t("Uncertain"), same: false },
                        ],
                    },
                    {
                        visible: true,
                        key: "drugs",
                        name: i18n.t("TargetDrug"),
                        option: [{ key: "drugs", name: i18n.t("Quantity"), same: false }],
                    },
                    {
                        visible: true,
                        key: "references",
                        name: i18n.t("RelatedReferences"),
                        option: [{ key: "references", name: i18n.t("Quantity"), same: false }],
                    },
                ],
            };

        default:
            return {
                name: i18n.t("GeneticComparison"),
                route: "Gene",
                error: i18n.t("AddedSameGene"),
                placeholder: i18n.t("AddNewGene"),
                option: [
                    {
                        visible: true,
                        key: "info",
                        name: i18n.t("Genetic"),
                        option: [
                            { key: "chromosome", name: i18n.t("Chromosome"), explain: i18n.t("ChromosomeExplain"), same: false },
                            { key: "chromosome_site", name: i18n.t("ChromosomeSite", 2), explain: i18n.t("ChromosomeExplain"), same: false },
                            { key: "genome_site", name: i18n.t("GenomePosition"), same: false },
                            { key: "direction", name: i18n.t("Direction"), same: false },
                            { key: "length", name: i18n.t("Layout.TypeLength", { type: "Gene" }), same: false },
                            { key: "molecular", name: i18n.t("MolecularWeight"), explain: i18n.t("MolecularWeightExplain"), same: false },
                            { key: "orthologs", name: i18n.t("Orthologs"), same: false },
                            { key: "related_models", name: i18n.t("RelatedModels"), same: false, route: "Model", query: { module: "module1" } },
                        ],
                    },
                    {
                        visible: true,
                        key: "transcripts",
                        name: i18n.t("Transcript"),
                        option: [
                            { key: "transcript_id", name: i18n.t("TranscriptID"), same: false, query: { module: "module4" } },
                            { key: "pre_length", name: i18n.t("Length") + " (nt)", same: false },
                            { key: "exon", name: i18n.t("ExonCount"), same: false },
                            { key: "cds_length", name: "CDS (bp)", same: false },
                            { key: "protein_length", name: i18n.t("Length") + " (aa)", same: false },
                        ],
                    },
                    {
                        visible: true,
                        key: "expression",
                        name: i18n.t("GeneExpression", 2),
                        explain: i18n.t("GeneExpressionExplain"),
                        option: [
                            { key: "tissue", name: i18n.t("Tissue"), same: false },
                            { key: "cells", name: i18n.t("Cells"), same: false },
                        ],
                    },
                    {
                        visible: true,
                        key: "protein_interaction",
                        name: i18n.t("ProteinInteraction"),
                        option: [
                            {
                                key: "protein_interaction",
                                name: i18n.t("Acting", 2),
                                same: false,
                                explain: i18n.t("RelevanceScoreInParentheses"),
                                route: "Gene",
                                query: { module: "module6" },
                            },
                        ],
                    },
                    {
                        visible: true,
                        key: "related_diseases",
                        name: i18n.t("RelatedDiseases"),
                        option: [
                            { key: "related_diseases", name: i18n.t("RelatedDiseases"), same: false, route: "Disease", query: { module: "module3" } },
                        ],
                    },
                    {
                        visible: true,
                        key: "mutations",
                        name: i18n.t("Mutations"),
                        option: [
                            { key: "total", name: i18n.t("Statistics"), same: false },
                            { key: "pathogenic", name: i18n.t("Pathogenic"), same: false },
                            { key: "likely_pathogenic", name: i18n.t("LikelyPathogenic"), same: false },
                            { key: "likely_benign", name: i18n.t("LikelyBenign"), same: false },
                            { key: "benign", name: i18n.t("Benign"), same: false },
                            { key: "uncertain", name: i18n.t("Uncertain"), same: false },
                        ],
                    },
                    {
                        visible: true,
                        key: "drugs",
                        name: i18n.t("TargetDrug"),
                        option: [{ key: "drugs", name: i18n.t("Quantity"), same: false }],
                    },
                    {
                        visible: true,
                        key: "references",
                        name: i18n.t("RelatedReferences"),
                        option: [{ key: "references", name: i18n.t("Quantity"), same: false }],
                    },
                ],
            };
    }
};
